// components/Navbar.tsx
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";

interface NavbarProps {
  activeSection: number;
  setActiveSection: React.Dispatch<React.SetStateAction<number>>;
  scrollToSection: (sectionIndex: number) => void;
}

const navBg = "bg-[#EDF2FC]";

const Navbar: React.FC<NavbarProps> = ({
  activeSection,
  scrollToSection,
  setActiveSection,
}) => {
  // open = "should be shown as open" (controls CSS state)
  // visible = "mounted in DOM" (ensures exit animation)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileVisible, setMobileVisible] = useState(false);

  const headerRef = useRef<HTMLElement | null>(null);
  const firstLinkRef = useRef<HTMLButtonElement | null>(null);
  const [headerHeight, setHeaderHeight] = useState<number>(75); // default

  const isAboutActive = activeSection === 1 || activeSection === 2;
  const isProductActive = activeSection === 3;

  const isPricingActive = activeSection === 4;
  const isContactActive = activeSection === 5;

  const changeSection = (index: number) => {
    setActiveSection(index);
    scrollToSection(index);
  };

  // measure header height (handles 75px on base and 96px on 2xl)
  const measureHeader = () => {
    if (headerRef.current) {
      setHeaderHeight(headerRef.current.offsetHeight || 75);
    }
  };

  useLayoutEffect(() => {
    measureHeader();
  }, []);

  useEffect(() => {
    const onResize = () => measureHeader();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // lock body scroll while the drawer is **visible** (during enter + exit)
  useEffect(() => {
    if (mobileVisible) document.body.classList.add("overflow-hidden");
    else document.body.classList.remove("overflow-hidden");
    return () => document.body.classList.remove("overflow-hidden");
  }, [mobileVisible]);

  // focus first link when *open* completes
  useEffect(() => {
    if (mobileOpen) {
      // wait a tick so it's focusable after mount
      const id = window.requestAnimationFrame(() => {
        firstLinkRef.current?.focus();
      });
      return () => window.cancelAnimationFrame(id);
    }
  }, [mobileOpen]);

  // close on Escape
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeMobile();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const openMobile = () => {
    setMobileVisible(true);
    // allow a frame for mount before toggling transform (enables transition)
    requestAnimationFrame(() => setMobileOpen(true));
  };

  const closeMobile = () => {
    setMobileOpen(false);
  };

  const toggleMobile = () => {
    if (!mobileVisible) openMobile();
    else closeMobile();
  };

  // unmount after exit transition ends
  const handlePanelTransitionEnd: React.TransitionEventHandler<
    HTMLDivElement
  > = (e) => {
    if (e.propertyName !== "transform") return; // only on transform end
    if (!mobileOpen) setMobileVisible(false);
  };

  const NavLinksDesktop: React.FC = () => (
    <div className="hidden md:flex items-center space-x-4 text-sm font-semibold text-gray-700">
      <button
        onClick={() => changeSection(1)}
        className={`border-b-2 px-3 pb-2 hover:border-[#004AAD] mt-1 ${
          isAboutActive ? "border-[#004AAD]" : "border-transparent"
        }`}
      >
        About
      </button>
      <button
        onClick={() => changeSection(3)}
        className={`border-b-2 px-3 pb-2 hover:border-[#004AAD] mt-1 ${
          isProductActive ? "border-[#004AAD]" : "border-transparent"
        }`}
      >
        Product
      </button>
      
      <button
        onClick={() => changeSection(4)}
        className={`border-b-2 px-3 pb-2 hover:border-[#004AAD] mt-1 ${
          isPricingActive ? "border-[#004AAD]" : "border-transparent"
        }`}
      >
        Pricing
      </button>
      <button
        onClick={() => changeSection(5)}
        className={`border-b-2 px-3 ml-8 pb-2 hover:border-[#004AAD] mt-1 ${
          isContactActive ? "border-[#004AAD]" : "border-transparent"
        }`}
      >
        Contact us
      </button>
    </div>
  );

  return (
    <>
      <header
        ref={headerRef}
        className={`sticky top-0 z-[60] transition-shadow duration-300 ${navBg} h-[75px] 2xl:h-[96px] ${
          activeSection > 0 ? "shadow-md" : ""
        }`}
      >
        <div className="mx-auto flex items-center justify-between px-5 md:px-24 h-full">
          {/* Left: Logo + Links */}
          <div className="flex items-center gap-8">
            <div className="flex items-center gap-2">
              <img
                onClick={() => changeSection(0)}
                src="/logo_site.svg"
                alt="THE ARK Logo"
                className="h-18 md:h-20 w-auto cursor-pointer"
              />
            </div>

            <NavLinksDesktop />
          </div>

          {/* Right: Auth (desktop) */}
          <div className="hidden md:flex space-x-2">
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="/login"
              className="bg-white w-[6rem] text-sm 2xl:text-base items-center justify-center border border-[#97A1B2] text-black cursor-pointer py-2 font-medium rounded hover:bg-gray-100 transition flex"
            >
              Login
            </a>
            <a
              target="_blank"
              rel="noopener noreferrer"
              href="/register"
              className="bg-blue-600 w-[6rem] text-sm 2xl:text-base items-center justify-center text-white cursor-pointer py-2 font-medium rounded hover:bg-blue-700 transition flex"
            >
              Register
            </a>
          </div>

          {/* Mobile: animated icon */}
          <button
            aria-label={mobileVisible ? "Close menu" : "Open menu"}
            aria-expanded={mobileVisible}
            onClick={toggleMobile}
            className="md:hidden inline-flex items-center justify-center p-2 pr-0 rounded"
          >
            <span className="relative block h-6 w-7 overflow-visible">
              {/* top bar */}
              <span
                className={[
                  "absolute block h-[2px] w-[18px] rounded",
                  "transition-[transform,opacity] duration-200 ease-in-out origin-center",
                  mobileOpen
                    ? "translate-y-[12px] rotate-45"
                    : "translate-y-[5px] rotate-0",
                ].join(" ")}
                style={{ backgroundColor: "#0056C8", left: "0" }}
              />
              {/* middle bar */}
              <span
                className={[
                  "absolute block h-[2px] rounded",
                  "transition-[transform,opacity,width] duration-200 ease-in-out origin-center",
                  mobileOpen
                    ? "opacity-0 translate-y-[12px] w-[18px]"
                    : "opacity-100 translate-y-[11px] w-[14px]",
                ].join(" ")}
                style={{ backgroundColor: "#0056C8", left: "0" }}
              />
              {/* bottom bar */}
              <span
                className={[
                  "absolute block h-[2px] w-[18px] rounded",
                  "transition-[transform,opacity] duration-200 ease-in-out origin-center",
                  mobileOpen
                    ? "translate-y-[12px] -rotate-45"
                    : "translate-y-[17px] rotate-0",
                ].join(" ")}
                style={{ backgroundColor: "#0056C8", left: "0" }}
              />
            </span>
          </button>
        </div>
      </header>

      {/* Mobile Drawer (mounted while visible, animates in/out) */}
      {mobileVisible && (
        <>
          {/* Backdrop */}
          <button
            aria-hidden="true"
            onClick={closeMobile}
            className={[
              "fixed inset-0 z-[50] bg-black/30",
              "transition-opacity duration-300 ease-out",
              "motion-reduce:transition-none",
              mobileOpen ? "opacity-100" : "opacity-0",
            ].join(" ")}
          />

          {/* Sliding panel */}
          <div
            role="dialog"
            aria-modal="true"
            aria-label="Mobile navigation"
            className={[
              "fixed right-0 z-[51] w-[100vw]",
              "bg-white shadow-2xl",
              "transition-transform duration-300 ease-out",
              "motion-reduce:transition-none",
              mobileOpen ? "translate-x-0" : "translate-x-full",
            ].join(" ")}
            style={{
              top: `${headerHeight}px`,
              height: `calc(100vh - ${headerHeight}px)`,
            }}
            onTransitionEnd={handlePanelTransitionEnd}
          >
            <nav className="flex flex-col items-stretch gap-2 p-6">
              <button
                ref={firstLinkRef}
                onClick={() => {
                  changeSection(1);
                  closeMobile();
                }}
                className={`text-lg text-center font-semibold rounded px-3 py-2  ${
                  isAboutActive ? "text-blue-600" : "text-gray-900"
                }`}
              >
                About
              </button>

              <button
                onClick={() => {
                  changeSection(3);
                  closeMobile();
                }}
                className={`text-lg text-center font-semibold rounded px-3 py-2  ${
                  isProductActive ? "text-blue-600" : "text-gray-900"
                }`}
              >
                Product
              </button>

              <button
                onClick={() => {
                  changeSection(4);
                  closeMobile();
                }}
                className={`text-lg text-center font-semibold rounded px-3 py-2  ${
                  isPricingActive ? "text-blue-600" : "text-gray-900"
                }`}
              >
                Pricing
              </button>

              <button
                onClick={() => {
                  changeSection(5);
                  closeMobile();
                }}
                className={`text-lg text-center font-semibold rounded px-3 py-2  ${
                  isContactActive ? "text-blue-600" : "text-gray-900"
                }`}
              >
                Contact Us
              </button>

              <a
                // target="_blank"
                // rel="noopener noreferrer"
                href="/login"
                className={`text-lg text-center font-semibold rounded px-3 py-2
                text-blue-600
                `}
              >
                Login
              </a>

              <a
                // target="_blank"
                // rel="noopener noreferrer"
                href="/register"
                className={`text-lg text-center font-semibold rounded px-3 py-2  text-blue-600`}
              >
                Register
              </a>
            </nav>
          </div>
        </>
      )}
    </>
  );
};

export default Navbar;
